import {
    AbilityBuilder,
    createMongoAbility,
    ForbiddenError,
} from '@casl/ability';
import { XOR } from 'ts-xor';

import { UserTypes } from '@barboxx/types';
import type {
    Abilities,
    AppAbility,
    Customer,
    User,
} from '@barboxx/models/types';

// // Permissions
export const hasInternalPermission = (userType: UserTypes): boolean => {
    return userType === 'Internal';
};

ForbiddenError.setDefaultMessage(
    error => `You are not allowed to ${error.action} on ${error.subjectType}`
);

export function defineAbilityFor(
    user?: XOR<User.Auth, Customer.Auth>
): AppAbility {
    if (user) {
        return createMongoAbility(user.rules);
    }

    return createMongoAbility<Abilities>();
}

export function defineRulesFor(user?: XOR<User.Auth, Customer.Auth>) {
    const builder = new AbilityBuilder<AppAbility>(createMongoAbility);

    if (!user) {
        defineAnonymousRules(builder);
        return builder.rules;
    }

    if (user?.userType) {
        switch (user.userType) {
            case 'Internal':
                defineInternalRules(builder, user);
                break;
            case 'Business':
                defineBusinessRules(builder, user);
                break;
            default:
                defineAnonymousRules(builder);
        }
    } else {
        defineConsumerRules(builder, user);
    }

    // builder.build({
    // resolveAction,
    // });

    return builder.rules;
}

function defineInternalRules(
    { can, cannot }: AbilityBuilder<AppAbility>,
    user: User.Auth
) {
    if (user.roles?.includes('Admin')) {
        can('manage', 'all');
    } else {
        can('read', 'all');
    }
    cannot('create', 'BusinessStripeAccount');
}

function defineBusinessRules(
    { can }: AbilityBuilder<AppAbility>,
    user: User.Auth
) {
    const { roles = [], businesses = [] } = user;
    const isAdmin = roles.includes('Admin');
    const isManager = roles.includes('Manager');
    const isOwner = roles.includes('Owner');

    const canUpdateStuff = isAdmin || isManager || isOwner;

    can('update', 'Order', {
        businessId: {
            $in: businesses,
        },
    });
    can(canUpdateStuff ? 'update' : 'read', 'Place', {
        businessId: { $in: businesses },
    });
    can('read', 'Business', {
        id: { $in: businesses },
    });
    if (isOwner) {
        can('create', 'BusinessStripeAccount', {
            businessId: { $in: businesses },
        });
    }
    can(canUpdateStuff ? 'manage' : 'read', 'User', {
        businesses: {
            $elemMatch: {
                id: {
                    $in: businesses,
                },
            },
        },
    });
    can('manage', 'User', {
        id: user.id,
    });
    can(canUpdateStuff ? 'manage' : 'read', 'BusinessLocker', {
        businessId: { $in: businesses },
    });
}

function defineConsumerRules(
    { can }: AbilityBuilder<AppAbility>,
    user: Customer.Auth
) {
    can('manage', 'Order', {
        createdBy: user.id,
    });
    can('manage', 'Customer', {
        id: user.id,
    });
    can('read', 'MenuItem', {
        isAlcoholic: user.over21,
    });

    if (user?.over21) {
        can('read', 'Alcohol');
    }
}

function defineAnonymousRules({ can }: AbilityBuilder<AppAbility>) {
    can('read', 'Place');
}
